// reports/ReportPage.tsx
//
// The shared shell for every report route: preview a report, then export it.
//
// What you see here is the document that gets printed — the same interpolated
// template HTML, in the same isolated frame, laid out by the same engine. The
// previous design kept two renderings of each report (a React component for the
// screen and a separate export path for the file) and they drifted: a fix to one
// had to be repeated in the other, and the exported PDF regularly did not match
// the preview it was generated from. Sharing one document makes that class of
// bug structurally impossible.
//
// The preview is an iframe for the same reason the exporter is: the template
// ships its own complete stylesheet, and letting the app's CSS cascade into a
// 794px fixed-width A4 sheet is what made the old preview unpredictable.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Loading from '../components/Loading';
import { exportReportPdf, renderReportHtml } from './renderReportPdf';
import type { TemplateContext } from './mustache';
import type { ReportKind } from './templateClient';

/** The template's fixed paper width. Previews scale down from this, never up. */
const PAPER_W = 794;
/** Fallback height until the frame reports its own. One A4 page @96dpi. */
const PAPER_H = 1123;

export interface ReportPageProps {
  kind: ReportKind;
  /** Resolves the view-model. Re-run whenever its identity changes. */
  load: () => Promise<TemplateContext | null>;
  /** Document title; the browser's default filename for the saved PDF. */
  title: string;
  /** Shown when `load` resolves null — e.g. the job is missing or unfinished. */
  emptyMessage?: string;
}

type State =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error'; message: string }
  | { status: 'ready'; html: string };

export function ReportPage({ kind, load, title, emptyMessage = 'Report not found.' }: ReportPageProps): React.JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(1);
  const [contentH, setContentH] = useState(PAPER_H);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // An AbortController rather than a boolean: the flag is flipped by the
    // cleanup function after an await, and `signal.aborted` is the one form of
    // that state control-flow analysis does not narrow away.
    const run = new AbortController();
    setState({ status: 'loading' });
    load()
      .then(async viewModel => {
        if (!viewModel) {
          if (!run.signal.aborted) setState({ status: 'empty' });
          return;
        }
        const html = await renderReportHtml(kind, viewModel);
        if (!run.signal.aborted) setState({ status: 'ready', html });
      })
      .catch((error: unknown) => {
        if (run.signal.aborted) return;
        setState({ status: 'error', message: error instanceof Error ? error.message : 'Could not build this report.' });
      });
    return () => { run.abort(); };
  }, [kind, load]);

  // Fit the fixed-width sheet into the viewport. The sheet is never enlarged:
  // above 794px it renders at its true print size.
  useEffect(() => {
    const fit = () => {
      const w = stageRef.current?.clientWidth ?? window.innerWidth;
      setScale(Math.min(1, (w - 24) / PAPER_W));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => { window.removeEventListener('resize', fit); };
  }, [state.status]);

  // The frame is transform-scaled, so its own box still occupies full height in
  // the layout; the stage has to reserve the scaled height explicitly or the
  // page ends up with a large gap (or a clipped sheet) below the preview.
  const onFrameLoad = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    const measure = () => {
      const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, PAPER_H);
      setContentH(h);
    };
    measure();
    // Images decode after load and can change the flow height.
    const timer = setTimeout(measure, 400);
    return () => { clearTimeout(timer); };
  }, []);

  const onExport = useCallback(async () => {
    if (state.status !== 'ready' || busy) return;
    setBusy(true);
    try {
      await exportReportPdf(state.html, { title });
    } finally {
      setBusy(false);
    }
  }, [state, busy, title]);

  if (state.status === 'loading') return <Loading />;

  if (state.status !== 'ready') {
    const message = state.status === 'empty' ? emptyMessage : state.message;
    return (
      <div style={{ padding: 'var(--s-10)', textAlign: 'center' }}>
        <p className="t-body t-mute">{message}</p>
      </div>
    );
  }

  return (
    <div ref={stageRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120, minHeight: '100vh' }}>
      <div style={{ height: scale * contentH + 32, width: '100%', display: 'flex', justifyContent: 'center' }}>
        <iframe
          ref={frameRef}
          title={title}
          srcDoc={state.html}
          onLoad={onFrameLoad}
          style={{
            width: PAPER_W,
            height: contentH,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            background: '#fff',
            boxShadow: '0 4px 12px rgba(31,36,33,.06), 0 24px 48px rgba(31,36,33,.10)',
          }}
        />
      </div>

      <PdfActionBar busy={busy} onExport={() => { void onExport(); }} />
    </div>
  );
}

/** Floating export bar, pinned to the visual viewport so it survives pinch-zoom
 *  and the mobile URL bar collapsing. */
export function PdfActionBar({ busy, onExport }: { busy: boolean; onExport: () => void }): React.JSX.Element {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => {
      const el = barRef.current;
      if (!el) return;
      const vp = window.visualViewport;
      if (vp) {
        el.style.top = `${vp.pageTop + vp.height}px`;
        el.style.width = `${vp.width * vp.scale}px`;
        el.style.transform = `translateY(-100%) scale(${1 / vp.scale})`;
      } else {
        el.style.top = `${window.scrollY + window.innerHeight}px`;
        el.style.width = '100%';
        el.style.transform = 'translateY(-100%)';
      }
    };
    update();
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, []);

  return (
    <div
      ref={barRef}
      className="pdf-bar absolute left-0 print:hidden"
      style={{ paddingBottom: 'max(var(--s-5), env(safe-area-inset-bottom))' }}
    >
      <button onClick={onExport} disabled={busy} className="pdf-bar__btn">
        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>picture_as_pdf</span>
        {busy ? 'Preparing…' : 'Download PDF'}
      </button>
    </div>
  );
}
